import { useCallback, useEffect, useRef, useState } from 'react';
import { records, permissions, providers as providersApi, audit, apiFetch } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import useInactivityLogout from './useInactivityLogout';

/** Purpose: Manages app auth/session lifecycle and initial data loading. Inputs: session config. Output: session state/actions. */
export default function useAppSession({
  transformRecord,
  transformPermission,
  setRecordsList,
  setPermissionsList,
  setProvidersList,
  setAuditLogs,
  clearUrlQueryState,
  toast,
  onLogoutCleanup
}) {
  const {
    user,
    setUser,
    emailVerified,
    setEmailVerified,
    showVerificationBanner,
    setShowVerificationBanner,
    sessionStartRef,
    firstUploadTrackedRef,
    handleLogout: authLogout,
    getHasSession
  } = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authView, setAuthView] = useState('login');
  const [resetToken, setResetToken] = useState(null);
  const [verifyToken, setVerifyToken] = useState(null);

  const handleLogout = useCallback(() => {
    authLogout();
    setAuthView('login');
    setResetToken(null);
    setVerifyToken(null);
    setError(null);
    onLogoutCleanup();
    clearUrlQueryState();
  }, [authLogout, clearUrlQueryState, onLogoutCleanup]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const profileRes = await apiFetch('/users/me');
      if (profileRes.status === 401) {
        throw new Error('UNAUTHORIZED');
      }
      if (!profileRes.ok) {
        throw new Error('Failed to fetch profile');
      }

      const profile = await profileRes.json();
      setUser(profile);
      if (profile.emailVerified !== undefined) {
        setEmailVerified(profile.emailVerified);
        setShowVerificationBanner(!profile.emailVerified);
      }

      const [recordsRes, permissionsRes, providersRes, auditRes] = await Promise.all([
        records.list(),
        permissions.list(),
        providersApi.list(),
        audit.list()
      ]);

      setRecordsList(recordsRes.data.map(transformRecord));
      firstUploadTrackedRef.current = (recordsRes.data || []).length > 0;
      setPermissionsList(permissionsRes.data.map(transformPermission));
      setProvidersList((providersRes.data || []));
      setAuditLogs(auditRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      if (err.message.includes('401') || err.message.includes('Unauthorized') || err.message.includes('UNAUTHORIZED')) {
        handleLogout();
      } else {
        setError('Failed to load data. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    firstUploadTrackedRef,
    handleLogout,
    setAuditLogs,
    setEmailVerified,
    setPermissionsList,
    setProvidersList,
    setRecordsList,
    setShowVerificationBanner,
    setUser,
    transformPermission,
    transformRecord
  ]);

  /** Purpose: Silently re-fetches all app data without showing the loading spinner. Inputs: none. Output: none. */
  const refreshData = useCallback(async () => {
    try {
      const [recordsRes, permissionsRes, providersRes, auditRes] = await Promise.all([
        records.list(),
        permissions.list(),
        providersApi.list(),
        audit.list()
      ]);

      setRecordsList(recordsRes.data.map(transformRecord));
      setPermissionsList(permissionsRes.data.map(transformPermission));
      setProvidersList(providersRes.data || []);
      setAuditLogs(auditRes.data);
    } catch (err) {
      console.error('Failed to refresh data:', err);
      if (err.message?.includes('401') || err.message?.includes('Unauthorized') || err.message?.includes('UNAUTHORIZED')) {
        handleLogout();
      }
    }
  }, [
    handleLogout,
    setAuditLogs,
    setPermissionsList,
    setProvidersList,
    setRecordsList,
    transformPermission,
    transformRecord
  ]);

  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;

  useEffect(() => {
    if (getHasSession()) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [getHasSession, loadData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const nextResetToken = params.get('token');
    if (nextResetToken && window.location.pathname.includes('reset')) {
      setResetToken(nextResetToken);
      setAuthView('reset');
      window.history.replaceState({}, '', window.location.pathname);
    }

    const verifyTokenParam = params.get('token');
    if (verifyTokenParam && window.location.pathname.includes('verify')) {
      setVerifyToken(verifyTokenParam);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleLogin = useCallback((data) => {
    sessionStartRef.current = Date.now();
    firstUploadTrackedRef.current = false;
    setUser(data.user || { authenticated: true });
    loadData();
  }, [firstUploadTrackedRef, loadData, sessionStartRef, setUser]);

  const handleResendVerification = useCallback(async () => {
    try {
      const response = await apiFetch('/auth/resend-verification', {
        method: 'POST'
      });
      if (!response.ok) {
        throw new Error('Failed to send verification email');
      }
      toast.success('Verification email sent');
    } catch (error) {
      console.error('Verification resend failed', error);
      toast.error('Failed to resend verification email');
    }
  }, [toast]);

  const handleInactivityLogout = useCallback(() => {
    toast.info('You were signed out due to inactivity.');
    handleLogout();
  }, [handleLogout, toast]);

  useInactivityLogout({
    enabled: Boolean(user),
    onTimeout: handleInactivityLogout
  });

  useEffect(() => {
    if (!verifyToken || !user) return;

    let cancelled = false;

    (async () => {
      try {
        const response = await apiFetch('/auth/verify-email', {
          method: 'POST',
          body: JSON.stringify({ token: verifyToken })
        });

        if (cancelled) return;

        if (response.ok) {
          setEmailVerified(true);
          setShowVerificationBanner(false);
          toast.success('Email verified successfully');
        } else {
          const data = await response.json();
          if (data.message === 'Email already verified') {
            setEmailVerified(true);
            setShowVerificationBanner(false);
          } else {
            toast.error(data.error || 'Verification failed');
          }
        }
      } catch (err) {
        if (!cancelled) {
          toast.error('Failed to verify email. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setVerifyToken(null);
          clearUrlQueryState();
        }
      }
    })();

    return () => { cancelled = true; };
  }, [clearUrlQueryState, setEmailVerified, setShowVerificationBanner, toast, user, verifyToken]);

  return {
    user,
    setUser,
    sessionStartRef,
    firstUploadTrackedRef,
    emailVerified,
    showVerificationBanner,
    setShowVerificationBanner,
    isLoading,
    error,
    setError,
    authView,
    setAuthView,
    resetToken,
    setResetToken,
    verifyToken,
    setVerifyToken,
    handleLogin,
    handleLogout,
    handleResendVerification,
    loadData,
    refreshData
  };
}
